// code-guardian-ignore-duplicates: deliberately-parallel report page — shares the
// header/filter/tile/table scaffolding of LmsJourneyReportPage by design so the Grow
// reports look and behave alike. What is NOT shared is what a row is: a journey report
// row is a person on a journey; here it is a module (All modules), or a person on the
// one module picked.
import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import {
  AlarmClock, ArrowLeft, BookOpenCheck, CheckCircle, Clock, Download, Search, XCircle, type LucideIcon,
} from 'lucide-react';
import {
  lmsAPI, type LmsModuleLearner, type LmsModuleLearnerCounts, type LmsModuleLearnersData,
} from '../services/api';
import { useAuthStore } from '../stores/authStore';
import { InlineLoader } from '../components/loading';
import { useTableSort, SortTh, compareSortText } from '../components/dashboard/useTableSort';
import { downloadTableXlsx, fileSlug, type TableCell } from '../utils/tableXlsx';

/**
 * Reports → Module Completion. It was called "Content Modules", the name the Grow page
 * that builds the modules also has; its address and its download keep the old word.
 *
 * It opens on "All modules": one row per content module of the project, with how
 * many people completed it, how many are pending, overdue and failed. Picking a
 * module (from the Module select, or by its title in the table) lists that
 * module's people and where each of them stands. The module picked is kept in the
 * address (?module=<id>), so a link or a refresh opens the same list.
 *
 * Who is listed for a module depends on the module. One with its own "Complete
 * within N days" rule is expected of everybody in the project. One without that
 * rule lists the people it was assigned to by name or by role. Anybody else who
 * opened or finished it is listed too, marked "Not assigned". A module with
 * neither lists only the people who started or finished it.
 *
 * So on All modules a module with neither gets no "% completed": nobody is
 * expected to complete it, and "3 of 3" would suggest a target that does not
 * exist. Its People cell reads "3 opened it" instead.
 *
 * Nothing is changed from here: it is a report. So the tables have no Actions.
 */

const REPORT_PATH = '/reports/content-modules';

type ModuleInfo = LmsModuleLearnersData['module'];
type Audience = LmsModuleLearnersData['audience'];

/** The group a person is counted in. "Pending" = not finished and not overdue. */
type Bucket = 'completed' | 'pending' | 'overdue' | 'failed';
type StatusFilter = 'all' | Bucket;

// A failed quiz cannot be taken again, so "failed" is finished too: it is never overdue.
const bucketOf = (row: LmsModuleLearner): Bucket => {
  if (row.state === 'completed') return 'completed';
  if (row.state === 'failed') return 'failed';
  return row.overdue ? 'overdue' : 'pending';
};

// The four groups, in the order of the tiles and of the Status select.
const BUCKETS: Array<{ key: Bucket; label: string; icon: LucideIcon; tone: string; meaning: string }> = [
  { key: 'completed', label: 'Completed', icon: CheckCircle, tone: 'text-emerald-700', meaning: '' },
  { key: 'pending', label: 'Pending', icon: Clock, tone: 'text-gray-700', meaning: 'Not finished, not yet due' },
  { key: 'overdue', label: 'Overdue', icon: AlarmClock, tone: 'text-red-700', meaning: 'Not finished, past the due date' },
  { key: 'failed', label: 'Failed', icon: XCircle, tone: 'text-orange-700', meaning: 'Did not pass' },
];

// The main tag, and a second, smaller one where there is a second fact to tell: a
// completion that came after the due date, or how far an overdue person has got.
const statusLabel = (row: LmsModuleLearner): string => {
  if (row.state === 'completed') return 'Completed';
  if (row.state === 'failed') return 'Failed';
  if (row.overdue) return 'Overdue';
  return row.state === 'in_progress' ? 'In progress' : 'Not started';
};
const secondTag = (row: LmsModuleLearner): { text: string; className: string } | null => {
  if (row.state === 'completed' && row.late) return { text: 'Late', className: 'bg-amber-100 text-amber-800' };
  if (row.state !== 'completed' && row.state !== 'failed' && row.overdue) {
    return row.state === 'in_progress'
      ? { text: 'In progress', className: 'bg-blue-100 text-blue-700' }
      : { text: 'Not started', className: 'bg-gray-100 text-gray-700' };
  }
  return null;
};
/** Both tags as one piece of text, for sorting and for the Excel file. */
const statusText = (row: LmsModuleLearner): string => {
  const second = secondTag(row);
  return second ? `${statusLabel(row)} · ${second.text}` : statusLabel(row);
};

const statusClass = (row: LmsModuleLearner): string => {
  if (row.state === 'completed') return 'bg-green-100 text-green-700';
  if (row.state === 'failed') return 'bg-orange-100 text-orange-700';
  if (row.overdue) return 'bg-red-100 text-red-700';
  return row.state === 'in_progress' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700';
};

// A module's own status, worded and coloured as on Grow → Content Modules.
const MODULE_STATUS: Record<string, { label: string; className: string }> = {
  draft: { label: 'Draft', className: 'bg-yellow-100 text-yellow-700' },
  published: { label: 'Published', className: 'bg-green-100 text-green-700' },
  archived: { label: 'Archived', className: 'bg-gray-100 text-gray-700' },
};
const moduleStatus = (status: string): { label: string; className: string } =>
  MODULE_STATUS[status] ?? { label: status, className: 'bg-gray-100 text-gray-700' };

// The API may send timestamps as "2026-10-07 12:15:00+05:30"; not every browser
// parses the space-separated form.
const toDate = (value: string | null): Date | null => {
  if (!value) return null;
  const iso = new Date(value.includes('T') ? value : value.replace(' ', 'T'));
  if (!Number.isNaN(iso.getTime())) return iso;
  const loose = new Date(value);
  return Number.isNaN(loose.getTime()) ? null : loose;
};

// Shown in India time wherever the page is opened: the due date beside it is an
// India day, and "late" is judged against that day.
const INDIA = 'Asia/Kolkata';
const DAY: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' };

const formatDateTime = (value: string | null): string => {
  const date = toDate(value);
  if (!date) return '';
  return `${date.toLocaleDateString('en-IN', { ...DAY, timeZone: INDIA })}, ${date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: INDIA })}`;
};

// A calendar day ('2026-10-13') as "13 Oct 2026", written the same way as the
// day in "Completed on". The digits are read off the string and kept as they
// are, so no browser can move a date-only value to the day before.
const formatDay = (day: string | null): string => {
  const m = day ? /^(\d{4})-(\d{2})-(\d{2})/.exec(day) : null;
  if (!m) return '';
  const date = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12));
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-IN', { ...DAY, timeZone: 'UTC' });
};

const toNumber = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/** A quiz score as a whole number of percent; null when there is none yet. */
const scoreOf = (row: LmsModuleLearner): number | null => {
  const n = toNumber(row.score);
  return n === null ? null : Math.round(n);
};

/** 250 → "4 min 10 s". Empty when no reading time was recorded. */
const formatTimeSpent = (value: number | null): string => {
  const n = toNumber(value);
  if (n === null || n < 0) return '';
  const total = Math.round(n);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  if (hours > 0) return minutes > 0 ? `${hours} h ${minutes} min` : `${hours} h`;
  if (minutes > 0) return seconds > 0 ? `${minutes} min ${seconds} s` : `${minutes} min`;
  return `${seconds} s`;
};

// How the module reached this person.
const assignedText = (row: LmsModuleLearner): string => {
  if (row.via === 'named') return 'By name';
  if (row.via === 'role') return row.via_label ? `Role: ${row.via_label}` : 'By role';
  if (row.via === 'everyone') return 'Everyone in the project';
  return 'Not assigned';
};

const count = (n: number): string => n.toLocaleString('en-IN');
const people = (n: number): string => `${count(n)} ${n === 1 ? 'person' : 'people'}`;
const modulesText = (n: number): string => `${count(n)} ${n === 1 ? 'module' : 'modules'}`;
/** A count, or a dash where there is none to show. */
const countOrDash = (n: number | null): string => (n === null ? '—' : count(n));

/** Who is listed, in a few words beside the module's name. */
const audienceShort = (audience: Audience): string =>
  audience === 'everyone' ? 'Everyone in the project' : audience === 'assigned' ? 'Assigned learners' : 'Not assigned';

/** Who is listed, in one sentence. */
const audienceLine = (audience: Audience, module: ModuleInfo, expected: number, capped: boolean): string => {
  if (audience === 'everyone') {
    const days = toNumber(module.due_within_days);
    return days && days > 0
      ? `Everyone in the project is expected to complete this module within ${days} ${days === 1 ? 'day' : 'days'}.`
      : 'Everyone in the project is expected to complete this module.';
  }
  if (audience === 'assigned') {
    // A list that was cut short cannot say how many people that is.
    return capped ? 'Assigned to people by name or by role.' : `Assigned to ${people(expected)}.`;
  }
  return 'This module is not assigned to anyone and has no "Complete within" days set, so only people who have started or completed it are listed.';
};

/** A module as the Module select and the All-modules table need it. */
interface ModuleOption {
  id: string;
  title: string;
  status: string;
  /** "Complete within N days"; null = the module has no rule of its own. */
  dueWithinDays: number | null;
}

/** A module's title as the page writes it: on screen, in the select and in the Excel file. */
const titleOf = (module: { title: string }): string => module.title || 'Untitled module';

/**
 * One row of the All-modules table. A count is null where there is none to show:
 * nobody is counted for the module yet (then `people` is 0), or the counts could
 * not be loaded (then `people` is null too).
 */
interface ModuleRow extends ModuleOption {
  /**
   * Is anybody expected to complete it? Yes when it is assigned (by name or by
   * role), or published with "Complete within" days (then everyone in the project
   * is). Otherwise the people counted are only those who opened it themselves.
   */
  expectsSomebody: boolean;
  people: number | null;
  completed: number | null;
  pending: number | null;
  overdue: number | null;
  failed: number | null;
  /** Completed out of people, as a whole number of percent. Null too where nobody is expected to complete it. */
  percent: number | null;
}

/**
 * Completed out of people, as a whole number of percent. It reads 100 only when
 * everybody has completed and 0 only when nobody has: 299 of 300 is 99%, not
 * 100%, and 1 of 300 is 1%, not 0%.
 */
const percentOf = (completed: number, of: number): number => {
  if (completed <= 0) return 0;
  if (completed >= of) return 100;
  return Math.min(99, Math.max(1, Math.round((completed * 100) / of)));
};

const moduleRowOf = (module: ModuleOption, counts: LmsModuleLearnerCounts | null, assignments: number): ModuleRow => {
  const expectsSomebody = assignments > 0 || (module.status === 'published' && (module.dueWithinDays ?? 0) > 0);
  const none = { completed: null, pending: null, overdue: null, failed: null, percent: null };
  if (!counts) return { ...module, expectsSomebody, people: null, ...none };
  const counted = counts[module.id];
  const expected = toNumber(counted?.expected) ?? 0;
  if (!counted || expected <= 0) return { ...module, expectsSomebody, people: 0, ...none };
  const completed = toNumber(counted.completed) ?? 0;
  return {
    ...module,
    expectsSomebody,
    people: expected,
    completed,
    pending: toNumber(counted.pending) ?? 0,
    overdue: toNumber(counted.overdue) ?? 0,
    failed: toNumber(counted.failed) ?? 0,
    percent: expectsSomebody ? percentOf(completed, expected) : null,
  };
};

/** True for a module nobody is expected to complete that some people opened all the same. */
const openedOnly = (row: ModuleRow): boolean => !row.expectsSomebody && (row.people ?? 0) > 0;

const OPENED_ONLY_TITLE = 'Not assigned to anyone and no Complete within days: these people opened it themselves';

/**
 * The People cell of a module's row, as the table shows it and as the Excel file
 * holds it: the count, or "3 opened it" where nobody is expected to complete the module.
 */
const peopleCell = (row: ModuleRow): number | string | null =>
  (openedOnly(row) ? `${count(row.people ?? 0)} opened it` : row.people);

/** One of the four numbers above the table. */
interface Tile {
  key: Bucket;
  label: string;
  icon: LucideIcon;
  tone: string;
  value: string;
  hint: string;
}

// What the server said about one module of one project. `data` is null when it
// could not be loaded, and `error` then says why.
interface LearnersAnswer {
  key: string;
  data: LmsModuleLearnersData | null;
  error: string | null;
}

/** The day in India, so a file made before 05:30 does not carry yesterday's date. */
const todayInIndia = (): string => new Date().toLocaleDateString('en-CA', { timeZone: INDIA });

// A status tag, in the shape the Journey Progress report gives its own. The colour is added to it.
const TAG_CLASS = 'inline-block px-2 py-0.5 rounded text-xs font-medium';

const SELECT_CLASS =
  'w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50 disabled:text-gray-400';

export default function LmsModuleReportPage() {
  const projectId = useAuthStore((state) => state.selectedProjectId) ?? undefined;

  // The module picked lives in the address, so a link or a refresh keeps it.
  const [searchParams, setSearchParams] = useSearchParams();
  const moduleId = (searchParams.get('module') ?? '').trim() || null;
  const pickModule = (id: string | null) => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      if (id) next.set('module', id);
      else next.delete('module');
      return next;
    });
  };

  // A module belongs to one project. So the module in the address goes with the
  // project it was opened under: when another project is chosen in the top bar,
  // it is taken out of the address (in place, so Back does not return to it) and
  // the new project opens on All modules. The first project the page sees is not
  // a change: a link opened under any project still asks the server for its module.
  const projectOfAddress = useRef(projectId);
  useEffect(() => {
    if (projectId === undefined) return;
    const before = projectOfAddress.current;
    if (before === undefined || before === projectId || moduleId === null) {
      // Nothing to take out (or it is out now): from here the address goes with this project.
      projectOfAddress.current = projectId;
      return;
    }
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      next.delete('module');
      return next;
    }, { replace: true });
  }, [projectId, moduleId, setSearchParams]);

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  // Status and Employee narrow a module's people. They mean nothing for a list of
  // modules, so going back to All modules clears them.
  useEffect(() => {
    if (!moduleId) {
      setStatusFilter('all');
      setSearch('');
    }
  }, [moduleId]);

  /* ───────────────────────── the project's modules, and their counts ───────────────────────── */

  // Each is kept with the project it belongs to, so the modules of the project
  // just left are never shown under the project now open. `modules` / `counts`
  // null = could not be loaded.
  const [list, setList] = useState<{ projectId: number; modules: ModuleOption[] | null } | null>(null);
  const [counted, setCounted] = useState<{ projectId: number; counts: LmsModuleLearnerCounts | null } | null>(null);
  // Per module id, how many assignments it has (by name or by role). A module that is not in it has none.
  const [assignments, setAssignments] = useState<{ projectId: number; byModule: Record<string, number> } | null>(null);
  const listRequest = useRef(0);
  useEffect(() => {
    const request = ++listRequest.current;
    if (!projectId) return;
    (async () => {
      let found: ModuleOption[];
      try {
        const response = await lmsAPI.getContentModules({ projectId });
        if (request !== listRequest.current) return;
        const rows: Array<Record<string, unknown>> = response.data?.data ?? [];
        found = rows
          .map((m) => ({
            id: String(m.id ?? ''),
            title: String(m.title ?? ''),
            status: String(m.status || 'draft'),
            dueWithinDays: toNumber(m.due_within_days as number | string | null | undefined),
          }))
          .filter((m) => m.id !== '');
        setList({ projectId, modules: found });
      } catch {
        if (request === listRequest.current) setList({ projectId, modules: null });
        return;
      }
      // Which of them are assigned to somebody: asked once the modules are known,
      // because the call takes their ids. Should it fail, every module counts as
      // not assigned. That costs some modules their percentage, never the page.
      const byModule: Record<string, number> = {};
      if (found.length > 0) {
        try {
          const response = await lmsAPI.getAssignmentCounts(found.map((m) => m.id), projectId);
          if (request !== listRequest.current) return;
          const items: unknown = response.data?.data;
          if (Array.isArray(items)) {
            for (const item of items as Array<{ module_id?: unknown; count?: number | string | null } | null>) {
              if (item?.module_id != null) byModule[String(item.module_id)] = toNumber(item.count) ?? 0;
            }
          }
        } catch {
          // Left empty: see above.
        }
      }
      if (request === listRequest.current) setAssignments({ projectId, byModule });
    })();
    (async () => {
      try {
        const response = await lmsAPI.getModuleLearnerCounts(projectId);
        if (request !== listRequest.current) return;
        setCounted({ projectId, counts: response.data?.data ?? {} });
      } catch {
        // A failed count only costs the numbers: the modules are still listed, and can still be picked.
        if (request === listRequest.current) setCounted({ projectId, counts: null });
      }
    })();
  }, [projectId]);

  // undefined = still loading; null = could not be loaded.
  const modules = list && list.projectId === projectId ? list.modules : undefined;
  const counts = counted && counted.projectId === projectId ? counted.counts : undefined;
  const assigned = assignments && assignments.projectId === projectId ? assignments.byModule : undefined;
  // The table waits for all three: without the assignments a module's row could
  // read "3 opened it" for a moment and then turn into a percentage.
  const listLoading = modules === undefined || (modules !== null && (counts === undefined || assigned === undefined));

  // By title, for the Module select.
  const moduleOptions = useMemo(
    () => [...(modules ?? [])].sort((a, b) => compareSortText(a.title, b.title) || compareSortText(a.id, b.id)),
    [modules],
  );

  // Overdue first (the most overdue on top), then by title: the modules to chase
  // lead the table until a heading is clicked.
  const moduleRows = useMemo(
    () => moduleOptions
      .map((module) => moduleRowOf(module, counts ?? null, assigned?.[module.id] ?? 0))
      .sort((a, b) => (b.overdue ?? 0) - (a.overdue ?? 0)),
    [moduleOptions, counts, assigned],
  );

  const moduleSort = useTableSort<ModuleRow>(moduleRows, {
    module: (r) => r.title,
    status: (r) => moduleStatus(r.status).label,
    people: (r) => r.people,
    completed: (r) => r.completed,
    pending: (r) => r.pending,
    overdue: (r) => r.overdue,
    failed: (r) => r.failed,
    percent: (r) => r.percent,
  });

  /* ─────────────────────────────── the people of the module picked ─────────────────────────────── */

  const [answer, setAnswer] = useState<LearnersAnswer | null>(null);
  const answerKey = projectId && moduleId ? `${projectId}:${moduleId}` : null;
  // Only the answer to the latest request is shown: a slow answer for the module
  // (or the project) just left must not replace the list now open.
  const requestRef = useRef(0);
  useEffect(() => {
    const request = ++requestRef.current;
    if (!projectId || !moduleId) {
      setAnswer(null);
      return;
    }
    // The project was just changed, and this module is on its way out of the
    // address (see above): the new project is not asked for the old one's module.
    if (projectOfAddress.current !== undefined && projectOfAddress.current !== projectId) {
      setAnswer(null);
      return;
    }
    const key = `${projectId}:${moduleId}`;
    (async () => {
      try {
        const response = await lmsAPI.getModuleLearners(moduleId, projectId);
        if (request !== requestRef.current) return;
        setAnswer({ key, data: response.data?.data ?? null, error: null });
      } catch (err) {
        if (request !== requestRef.current) return;
        const status = (err as { response?: { status?: number } })?.response?.status;
        // 400 is an address that is not a module's (a mistyped link): trying again cannot help.
        setAnswer({
          key,
          data: null,
          error: status === 404 || status === 400
            ? 'This module was not found in this project.'
            : 'Could not load the learners for this module. Please try again.',
        });
      }
    })();
  }, [moduleId, projectId]);

  // An answer counts only for the module and project it was asked for; until it
  // arrives the page says it is loading.
  const current = answer && answer.key === answerKey ? answer : null;
  const learnersLoading = answerKey !== null && current === null;
  const data = current?.data ?? null;
  const error = current?.error ?? null;

  const module = data?.module ?? null;
  const rows = useMemo(() => data?.learners ?? [], [data]);
  const audience: Audience = data?.audience ?? 'open';
  const capped = data?.capped === true;

  // A score is shown where there is a quiz, and for any other module (a SCORM course
  // keeps its own score) once somebody listed has one. Time spent is recorded only
  // for a module that asks for a minimum reading time.
  const showScore = module?.has_quiz === true || rows.some((row) => (scoreOf(row) ?? 0) > 0);
  const showTimeSpent = (toNumber(module?.min_time_seconds) ?? 0) > 0;

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (statusFilter !== 'all' && bucketOf(row) !== statusFilter) return false;
      if (!needle) return true;
      return (row.employee_name ?? '').toLowerCase().includes(needle) || row.employee_code.toLowerCase().includes(needle);
    });
  }, [rows, statusFilter, search]);

  const sort = useTableSort<LmsModuleLearner>(filtered, {
    employee: (r) => r.employee_name,
    code: (r) => r.employee_code,
    role: (r) => r.role_name,
    status: (r) => statusText(r),
    score: (r) => scoreOf(r),
    completed: (r) => toDate(r.completed_at)?.getTime() ?? null,
    due: (r) => r.due_date,
    assigned: (r) => assignedText(r),
    time: (r) => toNumber(r.time_spent_seconds),
  });

  // People the module was not meant for, who opened it anyway.
  const notAssigned = rows.filter((row) => row.via === 'none').length;
  const expected = rows.length - notAssigned;
  const totalPeople = toNumber(data?.total_people) ?? rows.length;

  // The server counts everybody the module concerns, before any cut. Should an
  // answer come without its counts, the people listed are counted instead.
  const summary = useMemo(() => {
    if (data?.summary) {
      return {
        total: toNumber(data.summary.total) ?? rows.length,
        completed: toNumber(data.summary.completed) ?? 0,
        pending: toNumber(data.summary.pending) ?? 0,
        overdue: toNumber(data.summary.overdue) ?? 0,
        failed: toNumber(data.summary.failed) ?? 0,
      };
    }
    const out = { total: rows.length, completed: 0, pending: 0, overdue: 0, failed: 0 };
    for (const row of rows) out[bucketOf(row)] += 1;
    return out;
  }, [data, rows]);
  // True when the four numbers speak for everybody, and not just for the people shown.
  const summaryIsWhole = summary.total === totalPeople;

  /* ───────────────────────────────────────── what is on screen ───────────────────────────────────────── */

  const allModules = moduleId === null;
  const modulesShown = allModules && !listLoading && modules != null && modules.length > 0;

  // One module: its own counts. All modules: the table's columns added up, so a
  // person listed on three modules is counted three times. The hints say so.
  //
  // Completed is a share ("30 of 83"), so on All modules both of its numbers leave
  // out the modules nobody is expected to complete: their people are no part of
  // any target, and counting only their completions would put more on top of the
  // share than under it. Its hint counts the modules it did add up. The other
  // three tiles are plain counts and add up every row.
  const openedOnlyRows = moduleRows.filter(openedOnly).length;
  let tiles: Tile[] = [];
  if (!allModules && data && module) {
    tiles = BUCKETS.map((b) => ({
      key: b.key,
      label: b.label,
      icon: b.icon,
      tone: b.tone,
      value: count(summary[b.key]),
      hint: b.key === 'completed' ? `of ${people(summary.total)}` : b.meaning,
    }));
  } else if (modulesShown) {
    const across = `across ${modulesText(moduleRows.length)}`;
    const expectedOf = moduleRows.filter((row) => !openedOnly(row));
    const sum = (from: ModuleRow[], pick: (row: ModuleRow) => number | null): number =>
      from.reduce((total, row) => total + (pick(row) ?? 0), 0);
    tiles = BUCKETS.map((b) => {
      const completed = b.key === 'completed';
      return {
        key: b.key,
        label: b.label,
        icon: b.icon,
        tone: b.tone,
        value: counts ? count(sum(completed ? expectedOf : moduleRows, (row) => row[b.key])) : '—',
        hint: !counts
          ? 'Counts not loaded'
          : completed
            ? `of ${count(sum(expectedOf, (row) => row.people))} across ${modulesText(expectedOf.length)}`
            : across,
      };
    });
  }

  // What the four numbers are, for whoever rests the pointer on them. One module's need no telling.
  const tilesTitle = !allModules
    ? undefined
    : `Every module added up: a person listed on three modules is counted three times.${
      openedOnlyRows > 0 ? ' Completed leaves out the modules nobody is expected to complete.' : ''}`;

  // The table as shown: same rows, same order, same columns.
  const canDownload = allModules ? modulesShown : module !== null && sort.sorted.length > 0;
  const handleDownload = () => {
    if (allModules) {
      if (!modulesShown) return;
      const body: TableCell[][] = moduleSort.sorted.map((r) => [
        titleOf(r), moduleStatus(r.status).label, peopleCell(r), r.completed, r.pending, r.overdue, r.failed, r.percent,
      ]);
      void downloadTableXlsx(
        `content-modules-${todayInIndia()}`,
        {
          // The sheet is named as the page is. The file keeps its old name.
          name: 'Module Completion',
          headers: ['Module', 'Status', 'People', 'Completed', 'Pending', 'Overdue', 'Failed', '% completed'],
          rows: body,
          // The share stays a number in Excel, written the way it reads on screen: 80%.
          numberFormats: { 7: '0"%"' },
        },
      );
      return;
    }
    if (!module) return;
    const headers = ['Employee', 'Employee Code', 'Role', 'Status'];
    if (showScore) headers.push('Score');
    headers.push('Completed on', 'Due date', 'Assigned via');
    if (showTimeSpent) headers.push('Time spent');
    const body: TableCell[][] = sort.sorted.map((r) => {
      const cells: TableCell[] = [r.employee_name, r.employee_code, r.role_name, statusText(r)];
      if (showScore) cells.push(scoreOf(r));
      cells.push(formatDateTime(r.completed_at), formatDay(r.due_date), assignedText(r));
      if (showTimeSpent) cells.push(formatTimeSpent(r.time_spent_seconds));
      return cells;
    });
    // A list that was cut short says so in the file's name: forwarded on its own, a
    // file of the first people only (the overdue come first) would read as "nobody completed".
    const partial = capped ? `-first-${rows.length}-of-${totalPeople}` : '';
    void downloadTableXlsx(
      `module-learners-${fileSlug(module.title)}${partial}-${todayInIndia()}`,
      // The score stays a number in Excel, written the way it reads on screen: 80%.
      { name: 'Learners', headers, rows: body, numberFormats: showScore ? { 4: '0"%"' } : undefined },
    );
  };

  // A module in the address that the list does not hold (a link to a module since
  // removed, or the list is still on its way) keeps a line of its own in the select.
  // Without it the select would read "All modules" and could not be used to go back there.
  // It carries the module's title once its own answer has it; until then "Loading…"
  // while the list is on its way, and "Selected module" for a list that came without it.
  const pickedIsListed = moduleId === null || moduleOptions.some((m) => m.id === moduleId);
  const pickedLabel = module?.title || (modules === undefined ? 'Loading…' : 'Selected module');

  // Status and Employee narrow a module's people, so they are open only while
  // such a list is on screen: not on All modules, and not while a module's
  // answer is loading or could not be loaded.
  const peopleListed = !allModules && data !== null && module !== null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <BookOpenCheck className="h-7 w-7 text-primary-600" />
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Module Completion</h1>
            <p className="text-sm text-gray-500 mt-1">
              Who has completed each content module, who is pending and who is overdue.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleDownload}
          disabled={!canDownload}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 shrink-0"
        >
          <Download className="w-4 h-4" />
          Download Excel
        </button>
      </div>

      {!projectId ? (
        <p className="py-16 text-center text-sm text-gray-500">Select a project to see this report.</p>
      ) : (
        <section className="rounded-lg border border-gray-100 bg-white p-6 space-y-6">
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="mr-module" className="block text-xs font-medium text-gray-600 mb-1">Module</label>
              <select
                id="mr-module"
                value={moduleId ?? 'all'}
                onChange={(e) => pickModule(e.target.value === 'all' ? null : e.target.value)}
                className={SELECT_CLASS}
              >
                <option value="all">All modules</option>
                {!pickedIsListed && moduleId && (
                  <option value={moduleId}>{pickedLabel}</option>
                )}
                {moduleOptions.map((m) => <option key={m.id} value={m.id}>{titleOf(m)}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="mr-status" className="block text-xs font-medium text-gray-600 mb-1">Status</label>
              <select
                id="mr-status"
                value={allModules ? 'all' : statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                disabled={!peopleListed}
                className={SELECT_CLASS}
              >
                <option value="all">All statuses</option>
                {BUCKETS.map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="mr-search" className="block text-xs font-medium text-gray-600 mb-1">Employee</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  id="mr-search"
                  type="text"
                  value={allModules ? '' : search}
                  onChange={(e) => setSearch(e.target.value)}
                  disabled={!peopleListed}
                  placeholder="Name or employee code"
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50 disabled:text-gray-400"
                />
              </div>
            </div>
          </div>

          {/* Summary */}
          {tiles.length > 0 && (
            <div
              role="group"
              aria-label="Counts"
              title={tilesTitle}
              className="grid grid-cols-2 lg:grid-cols-4 gap-3"
            >
              {tiles.map((t) => (
                <div key={t.key} className="rounded-lg border border-gray-200 p-4">
                  <div className="flex items-center gap-2 text-xs font-medium text-gray-500">
                    <t.icon className="w-3.5 h-3.5" />
                    {t.label}
                  </div>
                  <div className={`mt-1 text-2xl font-semibold tabular-nums ${t.tone}`}>{t.value}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{t.hint}</div>
                </div>
              ))}
            </div>
          )}

          {allModules ? (
            /* All modules: one row per module */
            listLoading ? (
              <InlineLoader label="Loading modules…" className="flex justify-center py-16" />
            ) : modules == null ? (
              <p className="py-16 text-center text-sm text-red-600">Could not load the content modules. Please try again.</p>
            ) : modules.length === 0 ? (
              <p className="py-16 text-center text-sm text-gray-500">This project has no content modules yet.</p>
            ) : (
              <>
                {/* What to do next, in the spot a module's name takes above its people:
                    so the four numbers sit in the same place in both views. */}
                {counts === null ? (
                  <p className="text-xs text-gray-500">
                    The counts could not be loaded, so the table shows dashes. You can still pick a module to see its people.
                  </p>
                ) : (
                  <p className="text-sm text-gray-500">Pick a module to see its people.</p>
                )}
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr className="whitespace-nowrap text-left text-xs font-medium text-gray-500">
                        <SortTh label="Module" sortKey="module" sort={moduleSort} className="px-4 py-3" />
                        <SortTh label="Status" sortKey="status" sort={moduleSort} className="px-4 py-3" title="Whether the module is published, a draft or archived" />
                        <SortTh label="People" sortKey="people" sort={moduleSort} align="right" className="px-4 py-3" title="Everyone the module concerns: the people expected to complete it, and anyone else who opened it" />
                        <SortTh label="Completed" sortKey="completed" sort={moduleSort} align="right" className="px-4 py-3" />
                        <SortTh label="Pending" sortKey="pending" sort={moduleSort} align="right" className="px-4 py-3" title="Not finished, and not yet past the due date" />
                        <SortTh label="Overdue" sortKey="overdue" sort={moduleSort} align="right" className="px-4 py-3" title="Not finished, and past the due date" />
                        <SortTh label="Failed" sortKey="failed" sort={moduleSort} align="right" className="px-4 py-3" />
                        <SortTh label="% completed" sortKey="percent" sort={moduleSort} align="right" className="px-4 py-3" title="Completed out of People. A dash for a module nobody is expected to complete." />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {moduleSort.sorted.map((r) => {
                        const status = moduleStatus(r.status);
                        const opened = openedOnly(r);
                        return (
                          <tr key={r.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <button
                                type="button"
                                onClick={() => pickModule(r.id)}
                                title="See this module's people"
                                className="text-left font-medium text-primary-600 hover:text-primary-700 hover:underline"
                              >
                                {titleOf(r)}
                              </button>
                            </td>
                            <td className="whitespace-nowrap px-4 py-3">
                              <span className={`${TAG_CLASS} ${status.className}`}>
                                {status.label}
                              </span>
                            </td>
                            <td
                              className={`px-4 py-3 text-right tabular-nums ${opened ? 'whitespace-nowrap text-gray-500' : 'text-gray-700'}`}
                              title={opened ? OPENED_ONLY_TITLE : undefined}
                            >
                              {opened ? peopleCell(r) : countOrDash(r.people)}
                            </td>
                            <td className="px-4 py-3 text-right tabular-nums text-gray-700">{countOrDash(r.completed)}</td>
                            <td className="px-4 py-3 text-right tabular-nums text-gray-700">{countOrDash(r.pending)}</td>
                            <td className={`px-4 py-3 text-right tabular-nums ${r.overdue ? 'font-medium text-red-700' : 'text-gray-700'}`}>{countOrDash(r.overdue)}</td>
                            <td className="px-4 py-3 text-right tabular-nums text-gray-700">{countOrDash(r.failed)}</td>
                            <td className="px-4 py-3 text-right tabular-nums text-gray-700">{r.percent === null ? '—' : `${r.percent}%`}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )
          ) : (
            /* One module: its people */
            learnersLoading ? (
              <InlineLoader label="Loading learners…" className="flex justify-center py-16" />
            ) : error || !module ? (
              <div className="py-16 text-center">
                <p className="text-sm text-red-600">{error ?? 'Module not found.'}</p>
                <button
                  type="button"
                  onClick={() => pickModule(null)}
                  className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to all modules
                </button>
              </div>
            ) : (
              <>
                <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
                  <h2 className="text-sm font-semibold text-gray-900">{`${module.title} · ${audienceShort(audience)}`}</h2>
                  <button
                    type="button"
                    onClick={() => pickModule(null)}
                    className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back to all modules
                  </button>
                </div>

                <div className="space-y-2">
                  {module.status !== 'published' && (
                    <p className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-sm text-gray-700">
                      This module is not published, so learners cannot open it in the app right now. The list shows where each
                      person stands.
                    </p>
                  )}
                  <p className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-2 text-sm text-blue-800">
                    {audienceLine(audience, module, expected, capped)}
                    {audience !== 'open' && !capped && notAssigned > 0 && (
                      <> Also listed: {people(notAssigned)} it was not assigned to, who opened it themselves.</>
                    )}
                  </p>
                  {capped && (
                    <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">
                      Showing the first {count(rows.length)} of {people(totalPeople)}. The Status and Employee filters and
                      the Excel download cover only the people shown.
                      {summaryIsWhole && <> The four numbers above count all {count(totalPeople)}.</>}
                    </p>
                  )}
                </div>

                {rows.length === 0 ? (
                  <p className="py-16 text-center text-sm text-gray-500">
                    {audience === 'everyone'
                      ? 'Nobody in this project has Grow content modules switched on for their role yet, so there is nobody to list.'
                      : audience === 'assigned'
                        ? 'The roles this module is assigned to have nobody in them yet.'
                        : 'Nobody has started this module yet.'}
                  </p>
                ) : filtered.length === 0 ? (
                  <p className="py-16 text-center text-sm text-gray-500">
                    {search.trim() ? `No one in this list matches "${search.trim()}".` : 'Nobody is in this group.'}
                  </p>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr className="whitespace-nowrap text-left text-xs font-medium text-gray-500">
                          <SortTh label="Employee" sortKey="employee" sort={sort} className="px-4 py-3" />
                          <SortTh label="Employee Code" sortKey="code" sort={sort} className="px-4 py-3" />
                          <SortTh label="Role" sortKey="role" sort={sort} className="px-4 py-3" />
                          <SortTh label="Status" sortKey="status" sort={sort} className="px-4 py-3" title="Overdue = not finished and past this person's due date. Late = finished after the due date." />
                          {showScore && <SortTh label="Score" sortKey="score" sort={sort} align="right" className="px-4 py-3" title="The score on record for this module" />}
                          <SortTh label="Completed on" sortKey="completed" sort={sort} className="px-4 py-3" />
                          <SortTh label="Due date" sortKey="due" sort={sort} className="px-4 py-3" title="This person's own due date: the same one the app shows them" />
                          <SortTh label="Assigned via" sortKey="assigned" sort={sort} className="px-4 py-3" title="How the module reached this person: by name, through a role, or because everyone in the project is expected to complete it" />
                          {showTimeSpent && <SortTh label="Time spent" sortKey="time" sort={sort} align="right" className="px-4 py-3" title="Time spent reading the module" />}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {sort.sorted.map((r) => {
                          const score = scoreOf(r);
                          const second = secondTag(r);
                          return (
                            <tr key={r.employee_code} className="hover:bg-gray-50">
                              <td className="whitespace-nowrap px-4 py-3 font-medium text-gray-900">{r.employee_name || '—'}</td>
                              <td className="whitespace-nowrap px-4 py-3 tabular-nums text-gray-600">{r.employee_code}</td>
                              <td className="px-4 py-3 text-gray-600">{r.role_name || '—'}</td>
                              <td className="whitespace-nowrap px-4 py-3">
                                <span className={`${TAG_CLASS} ${statusClass(r)}`}>
                                  {statusLabel(r)}
                                </span>
                                {second && (
                                  <span className={`ml-1 ${TAG_CLASS} ${second.className}`}>
                                    {second.text}
                                  </span>
                                )}
                              </td>
                              {showScore && (
                                <td className="px-4 py-3 text-right tabular-nums text-gray-600">{score === null ? '—' : `${score}%`}</td>
                              )}
                              <td className="whitespace-nowrap px-4 py-3 text-gray-600">{formatDateTime(r.completed_at) || '—'}</td>
                              <td className="whitespace-nowrap px-4 py-3 text-gray-600">{formatDay(r.due_date) || '—'}</td>
                              <td className="px-4 py-3 text-gray-600">{assignedText(r)}</td>
                              {showTimeSpent && (
                                <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums text-gray-600">{formatTimeSpent(r.time_spent_seconds) || '—'}</td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )
          )}
        </section>
      )}
    </div>
  );
}

/**
 * The address this report had while it lived under Grow → Content Modules:
 * /lms/content/:id/learners. Links and bookmarks to it open the report on that module.
 */
export function LmsModuleLearnersRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={id ? `${REPORT_PATH}?module=${encodeURIComponent(id)}` : REPORT_PATH} replace />;
}
